import {useEffect, useState} from 'react'

import btcIcon from '@Static/images/currencies/btc.png'
import cn from 'clsx'
import { useDispatch } from 'react-redux'

import { getValueWithCurrency } from '@App/helpers/toCurrency'
import { useAppSelector } from '@App/hooks'
import { addSuccessNotify } from '@App/redux/slices/notifications/NotificationSlice'
import { asyncGetTransactions, Transaction } from '@App/redux/slices/reports/ReportsSlice'
import { allTransactions } from '@App/redux/slices/reports/selectors'
import { currentWallet } from '@App/redux/slices/user/selectors'
import { MarketIcon } from '@App/ui/crypto_exchange/desktop/components/common/MarketIcon'
import { ToolTip } from '@App/ui/crypto_exchange/desktop/components/controls/ToolTip'

import {formatDate, formatTime, getTimestampFromPeriod, ReportsFilter, useCopyText} from '../../model'

import s from './styles.module.scss'

export const transaction_type_labels: Record<string, string> = {
  update_balance_deposit: 'set balance',
};

export const trade_type_labels: Record<string, string> = {
  bet: 'opened',
  win: 'closed',
  change: 'modified',
};

export const order_type_labels: Record<string, string> = {
  ...trade_type_labels,
  executed: "executed",
  win: "cancelled"
}

export const type_labels_map = {
  order: order_type_labels,
  trade: trade_type_labels,
}

export const getTransactionTitle = (transaction: Transaction) => {
  const trade_type =
    transaction.trade?.trading_bet_order?.execute_transaction_id &&
    transaction.trade?.trading_bet_order?.execute_transaction_id >= transaction.id
      ? "order" : transaction.trade?.type as keyof typeof type_labels_map | null;

  let result: string;

  if (trade_type == null) {
    result = transaction_type_labels[transaction.type];
  } else {
    const type = transaction.trade?.trading_bet_order?.execute_transaction_id === transaction.id ? "executed" : transaction.type
    result = `${trade_type} ${type_labels_map[trade_type][type]}`;
  }

  return result ? result.charAt(0).toUpperCase() + result.slice(1) : '';
};

export const TransactionsTable = ({ onRowClick, filters}: { onRowClick?: (val: Transaction, title: string) => void, filters: ReportsFilter }) => {
  const transactions = useAppSelector(allTransactions)
  const wallet = useAppSelector(currentWallet)
  const dispatch = useDispatch()
  const columns: { title: string; width: string }[] = [
    { title: 'Date', width: '2%' },
    { title: 'Time', width: '2%' },
    { title: 'Type', width: '10%' },
    { title: 'Info', width: '9%' },
    { title: 'Status', width: '15%' },
    { title: 'Result', width: '8%' },
    { title: 'Balance', width: '3%' },
    { title: 'ID', width: '1%' },
  ]

  const timestampFrom = getTimestampFromPeriod(filters.period)
  const args = {
    timestampInterval: {
      timestampFrom: timestampFrom,
      timestampTo:   null
    }
  }
  useEffect(() => {
    if (!wallet?.id) return
    dispatch(asyncGetTransactions(args))
  }, [wallet?.id, filters])

  const { copyText } = useCopyText()

  return (
    <div className={s.tableContainer}>
      <table className={s.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th style={{ width: col.width }} key={col.title} className={s[col.title.toLocaleLowerCase()]}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {transactions.map((transaction, index) => (
            <tr
              className={cn(s.row, s.fadeInRow)}
              key={transaction.id}
              style={{ animationDelay: `${index * 40}ms` }}
              onClick={() =>
                transaction.source_type === "trading" || transaction.destination_type === "trading"
                  ? onRowClick?.(transaction, getTransactionTitle(transaction)) :
                  undefined}
            >
              <td className={s.col}>{formatDate(transaction.transaction_time)}</td>
              <td className={s.col}>{formatTime(transaction.transaction_time)}</td>
              <td className={s.col}>{getTransactionTitle(transaction)}</td>
              <td className={cn(s.col, s.info)}>
                {transaction.market?.title &&
                  <>
                    <MarketIcon className={s.icon} symbol={transaction.market.symbol}
                    /> {transaction.market.title}
                  </>
                }
              </td>
              <td className={s.col}>Processed</td>
              <td className={cn(s.col, s.result, transaction.amount.amount < 0 && s.negative)}>
                {transaction.amount.currency_symbol}
                <span> {transaction.amount.amount}</span>
              </td>

              <td className={s.col}>
                {wallet?.currency_code && getValueWithCurrency(wallet?.currency_code, transaction.last_balance)}
              </td>
              <td className={s.col}>
                <ToolTip content={<span className={s.toolTip}>{transaction.front_id}</span>}>
                  <span className={s.idBadge} onClick={() => copyText(transaction.front_id)}>
                    ID
                  </span>
                </ToolTip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
